import React, { useState, useEffect, useMemo } from 'react';
import ProductCard from './ProductCard';
import VendorCard from './VendorCard';
import UiIcon from '../UiIcon';

// Currency formatter (Naira)
const formatNaira = (amount) => `₦${Number(amount || 0).toLocaleString()}`;

// Trust bar items
// Keep homepage trust messaging factual and capability-based.
// Avoid guarantees (e.g. "100% original", "7-day returns", "24/7") unless the
// underlying marketplace policy/data can prove them for the current order.
const TRUST_ITEMS = [
  { icon: 'store', title: 'Real Vendors', sub: 'Browse SchoolVerse vendors' },
  { icon: 'box', title: 'Products & Services', sub: 'Discover what you need' },
  { icon: 'mail', title: 'WhatsApp Fulfillment', sub: 'Stay connected with sellers' },
  { icon: 'arrowRight', title: 'Easy Sharing', sub: 'Share products & vendors' },
];

// Flash Deals Countdown Hook - counts down to real deal end (or end of day)
const useCountdown = (targetIso) => {
  const [time, setTime] = useState({ h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      let end;
      if (targetIso) {
        end = new Date(targetIso);
        if (isNaN(end.getTime())) end = null;
      }
      if (!end) {
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
      }
      const diff = Math.max(0, end - now);
      setTime({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return time;
};

// Small badge component for the countdown boxes
const CountdownBox = ({ value }) => (
  <span className="inline-flex items-center justify-center w-8 h-7 md:w-9 md:h-8 bg-red-600 text-white font-bold text-sm rounded">
    {String(value).padStart(2, '0')}
  </span>
);

// Section title with orange accent bar
const SectionTitle = ({ title, action, actionLabel }) => (
  <div className="flex items-center justify-between mb-4 px-1">
    <div className="flex items-center gap-3">
      <span className="inline-block w-1.5 h-6 bg-cyan-500 rounded-full" />
      <h2 className="text-xl md:text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    {action && (
      <button
        onClick={action}
        data-testid={`view-all-${title.toLowerCase().replace(/\s+/g, '-')}`}
        className="text-sm md:text-base text-cyan-700 hover:text-cyan-800 font-semibold whitespace-nowrap"
      >
        {actionLabel || 'View All'} <UiIcon name="arrowRight" size={14} className="inline-block ml-1" />
      </button>
    )}
  </div>
);

const MarketplaceHome = ({
  vendorTypes = [],
  activeBanners = {},
  vendors = [],
  homepageSections = [],
  homepageSettings = {},
  homepageFeaturedVendors = [],
  userLocation = null,
  flashDeals: apiFlashDeals = [],
  flashDealsNearestEnd = null,
  getImageUrl,
  navigateToCategoryPage,
  onSelectVendor,
  onSeeMoreVendors,
  onSelectProduct,
  onShareProduct,
  onShareVendor,
  currentCarouselIndex,
  setCurrentCarouselIndex,
  onSearchChange,
  onSearchSubmit,
  searchTerm,
  searchResults = [],
  searchVendors = [],
  searchCategories = [],
  isSearchingProducts = false,
  isProductSearchActive = false,
  onOpenAgentRegister,
  onOpenDeals,
}) => {
  const { h, m, s } = useCountdown(flashDealsNearestEnd);

  // Category list for left sidebar (max 12 + "See All")
  const sidebarCategories = useMemo(() => (vendorTypes || []).slice(0, 12), [vendorTypes]);

  // Flatten products from admin curated sections for Top Selling fallback
  const allSectionProducts = useMemo(() => {
    const acc = [];
    (homepageSections || []).forEach((sec) => {
      (sec.products || []).forEach((p) => acc.push(p));
    });
    return acc;
  }, [homepageSections]);

  // Only label products as Flash Deals when the backend has explicitly marked
  // them as active deals. Never infer a promotion from a curated product list.
  const flashDeals = (apiFlashDeals || []).slice(0, 12);
  // Curated sections are the source of truth for homepage merchandising.
  // Only show a generic fallback when there are no active curated sections,
  // preventing the same products from being duplicated above and below.
  const curatedSections = useMemo(
    () => (homepageSections || []).filter((section) =>
      section && section.is_active !== false && ((section.products || []).length > 0 || (section.vendors || []).length > 0)
    ),
    [homepageSections]
  );

  const featuredProducts = curatedSections.length === 0
    ? allSectionProducts.slice(0, 6)
    : [];

  // Carousel banners
  const topHeaderBanner = (activeBanners?.top_header || [])[0];
  const carouselBanners = activeBanners?.header_carousel || [];
  const slideshowBanners = activeBanners?.slideshow || [];

  return (
    <div className="bg-white" data-testid="marketplace-home">
      {/* Layout: Sidebar (desktop) + Main content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pt-4">
        <div className="flex gap-5">
          {/* MAIN CONTENT */}
          <div className="flex-1 min-w-0">
            {topHeaderBanner && (
              <div className="mb-4" data-testid="top-header-banner">
                <a href={topHeaderBanner.link_url || '#'} target={topHeaderBanner.link_url ? '_blank' : undefined} rel={topHeaderBanner.link_url ? 'noopener noreferrer' : undefined} className="block rounded-lg overflow-hidden">
                  <img src={getImageUrl(topHeaderBanner.image_url)} alt={topHeaderBanner.title || 'Promotional banner'} className="w-full max-h-28 object-cover" loading="eager" fetchPriority="high" decoding="async" />
                </a>
              </div>
            )}
            {/* HERO GRID — admin-controlled only. No banner means no hero/carousel section. */}
            {carouselBanners.length > 0 && (
              <section className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-5" data-testid="hero-section">
                <div className="md:col-span-3 relative rounded-xl overflow-hidden bg-gray-100 min-h-[220px] md:min-h-[320px]">
                  {carouselBanners.map((banner, idx) => (
                    <div key={banner.id || idx} className={`absolute inset-0 transition-opacity duration-700 ${idx === currentCarouselIndex ? 'opacity-100' : 'opacity-0'}`}>
                      {banner.link_url ? (
                        <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                          <img src={getImageUrl(banner.image_url)} alt={banner.title || 'Banner'} className="w-full h-full object-cover" loading={idx === currentCarouselIndex ? 'eager' : 'lazy'} fetchPriority={idx === currentCarouselIndex ? 'high' : 'auto'} decoding="async" />
                        </a>
                      ) : (
                        <img src={getImageUrl(banner.image_url)} alt={banner.title || 'Banner'} className="w-full h-full object-cover" loading={idx === currentCarouselIndex ? 'eager' : 'lazy'} fetchPriority={idx === currentCarouselIndex ? 'high' : 'auto'} decoding="async" />
                      )}
                    </div>
                  ))}
                  {carouselBanners.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                      {carouselBanners.map((_, i) => (
                        <button key={i} onClick={() => setCurrentCarouselIndex(i)} aria-label={`Show banner ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === currentCarouselIndex ? 'w-6 bg-cyan-500' : 'w-1.5 bg-white/60'}`} />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* SchoolVerse brand hero: shown when no admin carousel is configured. */}
            {carouselBanners.length === 0 && (
              <section className="relative overflow-hidden rounded-3xl mb-6 border border-cyan-100 bg-gradient-to-r from-cyan-50 via-white to-yellow-50" data-testid="schoolverse-hero">
                <div className="absolute -right-10 -top-14 h-64 w-64 rounded-full bg-yellow-300/70" aria-hidden="true" />
                <div className="absolute right-28 -bottom-20 h-44 w-44 rounded-full bg-cyan-100/90" aria-hidden="true" />
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-5 items-center p-5 sm:p-7 md:p-10">
                  <div className="relative z-10">
                    <span className="inline-flex items-center rounded-full bg-cyan-100 px-3 py-1.5 text-xs sm:text-sm font-bold text-cyan-900 mb-4">Student entrepreneurs. Bigger opportunities.</span>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-slate-950">Buy. Sell. Grow.<br /><span className="text-cyan-700">The student way.</span></h1>
                    <p className="mt-4 max-w-xl text-sm sm:text-base leading-relaxed text-slate-600">Discover great products and services from student entrepreneurs, support campus businesses, and grow your own brand on SchoolVerse.</p>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <button type="button" onClick={() => document.getElementById('featured-products-section')?.scrollIntoView({ behavior: 'smooth' })} className="rounded-full bg-yellow-400 px-5 py-3 text-sm font-extrabold text-slate-950 shadow-sm transition hover:bg-yellow-300">Explore marketplace <i className="fa-solid fa-arrow-right ml-2" /></button>
                      <button type="button" onClick={() => onOpenAgentRegister?.()} className="rounded-full border-2 border-cyan-700 bg-white/90 px-5 py-3 text-sm font-bold text-cyan-800 transition hover:bg-cyan-50">Become a student seller</button>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm font-semibold text-slate-600">
                      <span><i className="fa-solid fa-store text-cyan-700 mr-2" />Student-owned businesses</span>
                      <span><i className="fa-solid fa-location-dot text-cyan-700 mr-2" />Shop around your campus</span>
                    </div>
                  </div>
                  <div className="relative z-10 min-h-[190px] sm:min-h-[250px] md:min-h-[300px] overflow-hidden rounded-2xl bg-cyan-100">
                    <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1000&q=85" alt="Young entrepreneurs collaborating on a business" className="absolute inset-0 h-full w-full object-cover" loading="eager" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 rounded-2xl border border-white/30 bg-white/90 p-3 shadow-lg backdrop-blur-sm">
                      <p className="text-sm font-extrabold text-slate-900">Made on campus. Ready for the world.</p>
                      <p className="mt-1 text-xs text-slate-600">Find student brands worth discovering.</p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* TRUST BAR */}
            <section
              className="bg-white border border-gray-200 rounded-xl px-3 md:px-5 py-3 md:py-4 mb-6 overflow-x-auto"
              data-testid="trust-bar"
            >
              <div className="flex items-center justify-between gap-4 md:gap-6 min-w-max md:min-w-0">
                {TRUST_ITEMS.map((item) => (
                  <div key={item.title} className="flex items-center gap-2.5 md:gap-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-cyan-100 text-cyan-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <UiIcon name={item.icon} size={18} className="text-current" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-bold text-gray-900 leading-tight whitespace-nowrap">
                        {item.title}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-500 whitespace-nowrap">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* PRODUCT SEARCH RESULTS */}
            {isProductSearchActive && (
              <section className="mb-6" data-testid="product-search-results">
                <div className="flex items-center justify-between mb-4 px-1">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-1.5 h-6 bg-cyan-500 rounded-full" />
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                      Search Results
                    </h2>
                  </div>
                  {!isSearchingProducts && (
                    <span className="text-sm text-gray-500">
                      {searchResults.length} {searchResults.length === 1 ? 'product' : 'products'}
                    </span>
                  )}
                </div>

                {isSearchingProducts ? (
                  <div className="py-10 text-center text-gray-500">
                    <UiIcon name="spinner" size={24} className="mb-3 animate-spin" />
                    <p className="text-sm">Searching SchoolVerse...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                    {searchResults.map((product, idx) => (
                      <ProductCard
                        key={product.id || idx}
                        product={product}
                        getImageUrl={getImageUrl}
                        formatPrice={formatNaira}
                        onClick={() => onSelectProduct?.(product)}
                        onShare={onShareProduct}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-10 px-4 text-center bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-cyan-100 text-cyan-500 flex items-center justify-center">
                      <UiIcon name="box" size={22} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      No products found
                    </h3>
                    <p className="text-sm text-gray-500">
                      We couldn't find any products matching "{searchTerm}".
                    </p>
                  </div>
                )}
              </section>
            )}
            {isProductSearchActive && searchVendors.length > 0 && (
              <section className="mb-6" data-testid="search-store-results">
                <SectionTitle title="Vendors related to your search" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  {searchVendors.slice(0, 8).map((vendor) => (
                    <VendorCard key={vendor.id} vendor={vendor} getImageUrl={getImageUrl} onClick={onSelectVendor} onShare={(v, e) => onShareVendor(v, e)} actionLabel={vendor.is_open === false ? 'Closed' : 'Shop'} />
                  ))}
                </div>
              </section>
            )}

            {isProductSearchActive && searchCategories.length > 0 && (
              <section className="mb-6" data-testid="search-category-results">
                <SectionTitle title="Related categories" />
                <div className="flex flex-wrap gap-2">
                  {searchCategories.slice(0, 8).map((cat, i) => (
                    <button key={`${cat.label}-${i}`} type="button" onClick={() => cat.type ? navigateToCategoryPage(cat.type) : onSearchSubmit()} className="px-4 py-2 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-cyan-500 hover:text-cyan-700">
                      {cat.label}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* FLASH DEALS */}
            {flashDeals.length > 0 && (
              <section className="mb-6" data-testid="flash-deals-section">
                <div className="flex items-center justify-between mb-4 px-1 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-1.5 h-6 bg-red-500 rounded-full" />
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">Flash Deals</h2>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600 ml-2">
                      <span className="hidden md:inline">Ends in</span>
                      <CountdownBox value={h} />
                      <span className="font-bold">:</span>
                      <CountdownBox value={m} />
                      <span className="font-bold">:</span>
                      <CountdownBox value={s} />
                    </div>
                  </div>
                  <button
                    onClick={() => onOpenDeals?.()}
                    className="text-sm text-cyan-700 hover:text-cyan-800 font-semibold"
                  >
                    View All Deals <UiIcon name="arrowRight" size={14} className="inline-block ml-1" />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {flashDeals.map((p, idx) => (
                    <ProductCard
                      key={p.id || idx}
                      product={p}
                      getImageUrl={getImageUrl}
                      formatPrice={formatNaira}
                      onShare={onShareProduct}
                      onClick={() => onSelectProduct?.(p)}
                      showDiscount
                    />
                  ))}
                </div>
              </section>
            )}

            {/* RESTAURANT / PROMOTIONAL SLIDESHOW — deliberately below sales. */}
            {slideshowBanners.length > 0 && (
              <section className="mb-6" data-testid="restaurant-slideshow">
                <SectionTitle title="Featured Offers" />
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  {slideshowBanners.map((banner) => (
                    <a key={banner.id} href={banner.link_url || '#'} target={banner.link_url ? '_blank' : undefined} rel={banner.link_url ? 'noopener noreferrer' : undefined} className="block rounded-xl overflow-hidden bg-gray-100 aspect-[16/7]">
                      <img src={getImageUrl(banner.image_url)} alt={banner.title || 'Promotional banner'} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* TOP SELLING PRODUCTS */}
            {featuredProducts.length > 0 && (
              <section id="featured-products-section" className="mb-6" data-testid="featured-products-section">
                <SectionTitle title="Featured Picks" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {featuredProducts.map((p, idx) => (
                    <ProductCard
                      key={p.id || idx}
                      product={p}
                      getImageUrl={getImageUrl}
                      formatPrice={formatNaira}
                      onShare={onShareProduct}
                      onClick={() => onSelectProduct?.(p)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* ADMIN-CURATED MARKETPLACE SECTIONS */}
            {curatedSections.map((section, sectionIndex) => {
              const sectionVendors = (section.vendors || []).slice(0, section.max_items || 12);
              const sectionProducts = (section.products || []).slice(0, section.max_items || 12);
              const isSlider = section.display_mode === 'slider';
              const sectionBanners = (activeBanners?.between_cards || []).filter((banner) => String(banner.target_section_id || '') === String(section.id || ''));
              const renderSectionBanner = (position) => sectionBanners.filter((banner) => Number(banner.display_position || 4) === position).map((banner, bannerIndex) => (
                <a key={`section-banner-${banner.id}-${bannerIndex}`} href={banner.link_url || '#'} target={banner.link_url ? '_blank' : undefined} rel={banner.link_url ? 'noopener noreferrer' : undefined} className={`${isSlider ? 'min-w-[280px] sm:min-w-[420px]' : 'col-span-2 sm:col-span-3 md:col-span-4 lg:col-span-6'} block rounded-xl overflow-hidden bg-gray-100 aspect-[16/4]`}>
                  <img src={getImageUrl(banner.image_url)} alt={banner.title || 'Promotional banner'} className="w-full h-full object-cover" />
                </a>
              ));
              return (
                <section key={section.id || `curated-${sectionIndex}`} className="mb-8 rounded-xl border border-gray-200 overflow-hidden" style={section.background_color ? { backgroundColor: section.background_color } : undefined} data-testid={`homepage-section-${section.id || sectionIndex}`}>
                  <div className="px-3 md:px-5 pt-4 pb-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {section.icon && <UiIcon name={section.icon} size={20} className="text-gray-700 shrink-0" />}
                        <h2 className="text-xl md:text-2xl font-bold text-gray-900 truncate">{section.title}</h2>
                      </div>
                      {section.description && <p className="text-xs md:text-sm text-gray-500 mt-1">{section.description}</p>}
                    </div>
                    {section.show_view_all !== false && (sectionVendors.length > 0 || sectionProducts.length > 0) && (
                      <button onClick={() => sectionVendors.length ? document.getElementById('vendors-section')?.scrollIntoView({ behavior: 'smooth' }) : document.getElementById('featured-products-section')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-cyan-700 font-semibold whitespace-nowrap">See more <UiIcon name="arrowRight" size={14} className="inline-block ml-1" /></button>
                    )}
                  </div>

                  {sectionProducts.length > 0 && (
                    <div className="px-3 md:px-5 pb-4">
                      <div className={isSlider ? 'flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3'}>
                        {sectionProducts.map((product, idx) => (
                          <React.Fragment key={product.id || idx}>
                            <div className={isSlider ? 'min-w-[150px] sm:min-w-[190px] snap-start' : ''}>
                              <ProductCard product={product} getImageUrl={getImageUrl} formatPrice={formatNaira} onShare={onShareProduct} onClick={() => onSelectProduct?.(product)} />
                            </div>
                            {renderSectionBanner(idx + 1)}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}

                  {sectionVendors.length > 0 && (
                    <div className="px-3 md:px-5 pb-5">
                      <div className={isSlider ? 'flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3'}>
                        {sectionVendors.map((vendor, idx) => (
                          <React.Fragment key={vendor.id || idx}>
                            <div className={isSlider ? 'min-w-[160px] sm:min-w-[200px] snap-start' : ''}>
                              <VendorCard vendor={vendor} getImageUrl={getImageUrl} onClick={onSelectVendor} onShare={onShareVendor} compact />
                            </div>
                            {sectionProducts.length === 0 ? renderSectionBanner(idx + 1) : null}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}

            {/* EXPLICITLY ADMIN-FEATURED STORES */}
            {homepageFeaturedVendors.length > 0 && (
              <section id="vendors-section" className="mb-8" data-testid="vendors-section">
                <SectionTitle title={homepageSettings.featured_vendor_title || 'Featured Vendors'} action={homepageSettings.featured_vendor_show_view_all !== false ? onSeeMoreVendors : undefined} actionLabel="See more" />
                {(() => {
                  const chunks = [];
                  const list = homepageFeaturedVendors.slice(0, homepageSettings.featured_vendor_limit || 12);
                  const banners = activeBanners?.between_cards || [];
                  list.forEach((vendor, index) => {
                    chunks.push(vendor);
                    // Admin-configured placement; legacy banners default to after card 4.
                    const position = index + 1;
                    banners
                      .filter((banner) => Number(banner.display_position || 4) === position)
                      .forEach((banner) => chunks.push({ __banner: true, ...banner }));
                  });
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {chunks.map((item, idx) => item.__banner ? (
                        <a key={`between-${item.id}-${idx}`} href={item.link_url || '#'} target={item.link_url ? '_blank' : undefined} rel={item.link_url ? 'noopener noreferrer' : undefined} className="col-span-2 sm:col-span-3 md:col-span-4 lg:col-span-6 block rounded-xl overflow-hidden bg-gray-100 aspect-[16/4]">
                          <img src={getImageUrl(item.image_url)} alt={item.title || 'Promotional banner'} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                        </a>
                      ) : (
                        <VendorCard key={item.id || idx} vendor={item} getImageUrl={getImageUrl} onClick={onSelectVendor} onShare={onShareVendor} compact />
                      ))}
                    </div>
                  );
                })()}
                <p className="text-xs text-gray-500 mt-3">{userLocation?.name && userLocation.name !== 'all' ? `Showing vendors prioritized for ${userLocation.name}` : 'Showing vendors selected by SchoolVerse admin'}</p>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceHome;




